import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Briefcase, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { adminListJobs, adminCreateJob, adminUpdateJob, adminDeleteJob } from "@/lib/apiClient";

const inputCls = "bg-[hsl(220_30%_8%)] border-border focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]";
const empty = { title: "", department: "", location: "", type: "Full-time", summary: "", responsibilities: "", requirements: "" };

const toLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");
const fromLines = (str) => str.split("\n").map((s) => s.trim()).filter(Boolean);

export default function JobsPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = () => {
    setLoading(true);
    adminListJobs().then(setJobs).catch(() => toast.error("Failed to load jobs.")).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setForm(empty); setDialog({ mode: "create" }); };
  const openEdit = (job) => {
    setForm({ title: job.title, department: job.department, location: job.location, type: job.type || "Full-time", summary: job.summary, responsibilities: toLines(job.responsibilities), requirements: toLines(job.requirements) });
    setDialog({ mode: "edit", job });
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.title.trim() || !form.department.trim() || !form.location.trim() || !form.summary.trim()) {
      toast.error("Title, department, location and summary are required."); return;
    }
    setSaving(true);
    const payload = { ...form, responsibilities: fromLines(form.responsibilities), requirements: fromLines(form.requirements) };
    try {
      if (dialog.mode === "create") { await adminCreateJob(payload); toast.success("Job added."); }
      else { await adminUpdateJob(dialog.job.id, payload); toast.success("Job updated."); }
      setDialog(null); load();
    } catch { toast.error("Save failed."); } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await adminDeleteJob(toDelete.id); toast.success("Job deleted."); load(); }
    catch { toast.error("Delete failed."); } finally { setToDelete(null); }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Job openings</h2>
          <p className="mt-1 text-sm text-muted-foreground">Manage the roles shown on the Careers page.</p>
        </div>
        <Button onClick={openCreate} className="bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]" data-testid="job-add-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add role
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-3" data-testid="jobs-admin-list">
          {jobs.map((j) => (
            <div key={j.id} className="flex items-start justify-between gap-4 rounded-[var(--radius-card)] border border-border bg-card p-5" data-testid={`job-admin-item-${j.id}`}>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium">{j.title}</p>
                  <Badge variant="secondary" className="border-border bg-secondary text-xs text-muted-foreground">{j.department}</Badge>
                </div>
                <p className="mt-1.5 text-sm text-muted-foreground">{j.summary}</p>
                <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-primary" /> {j.location}</span>
                  <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3.5 w-3.5 text-primary" /> {j.type}</span>
                </div>
              </div>
              <div className="flex flex-shrink-0 gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(j)} data-testid={`job-edit-button-${j.id}`}><Pencil className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setToDelete(j)} data-testid={`job-delete-button-${j.id}`} className="text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-card sm:max-w-lg" data-testid="job-dialog">
          <DialogHeader><DialogTitle>{dialog?.mode === "create" ? "Add role" : "Edit role"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label className="text-sm">Title</Label><Input value={form.title} onChange={set("title")} className={inputCls} data-testid="job-title-input" /></div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2"><Label className="text-sm">Department</Label><Input value={form.department} onChange={set("department")} className={inputCls} data-testid="job-department-input" /></div>
              <div className="space-y-2"><Label className="text-sm">Type</Label><Input value={form.type} onChange={set("type")} className={inputCls} data-testid="job-type-input" /></div>
            </div>
            <div className="space-y-2"><Label className="text-sm">Location</Label><Input value={form.location} onChange={set("location")} className={inputCls} data-testid="job-location-input" /></div>
            <div className="space-y-2"><Label className="text-sm">Summary</Label><Textarea rows={2} value={form.summary} onChange={set("summary")} className={inputCls} data-testid="job-summary-input" /></div>
            <div className="space-y-2"><Label className="text-sm">Responsibilities <span className="text-muted-foreground">(one per line)</span></Label><Textarea rows={3} value={form.responsibilities} onChange={set("responsibilities")} className={inputCls} data-testid="job-responsibilities-input" /></div>
            <div className="space-y-2"><Label className="text-sm">Requirements <span className="text-muted-foreground">(one per line)</span></Label><Textarea rows={3} value={form.requirements} onChange={set("requirements")} className={inputCls} data-testid="job-requirements-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-[hsl(173_80%_34%)]" data-testid="job-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="border-border bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this role?</AlertDialogTitle>
            <AlertDialogDescription>This will remove it from the public Careers page. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="job-confirm-delete-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
